'use client';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'locomotive-scroll/dist/locomotive-scroll.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/effect-fade';
import 'swiper/css/grid';
import 'lightgallery.js/dist/css/lightgallery.css';
import 'haspr-cursor/dist/cursor.css';
import '@/styles/global.css';

import React, { useEffect, useState } from 'react';
import { theme, ThemeProvider } from 'styles/theme';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { SplitText } from 'gsap/SplitText';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';

import HasprCursor, { DispatchCursor } from 'haspr-cursor';
import ErrorBoundary from '@/src/components/meta/ErrorBoundary.jsx';
import Footer from '@/src/components/layout/Footer.jsx';
import GlobalStyle from '@/styles/global/globalStyle';
import MenuComponent from '@/src/components/layout/menu/Menu.jsx';
import SEO from '@/src/components/meta/SEO.jsx';
import StyledComponentsRegistry from '@/src/lib/registry.js';
import { Toaster } from 'sonner';

import getMenuData from '@/public/json/menu.json';
import getSettingsData from '@/public/json/settings.json';

import useAnimatedTextTitle from '@/src/hooks/animation/useAnimatedTextTitle.jsx';
import useAppEffects from '@/src/hooks/useAppEffects.jsx';
import useDeviceTypeMobile from '@/src/hooks/useDeviceTypeMobile.jsx';
import Loading from '@/src/components/common/ui/loading/Loading';
import { usePathname } from 'next/navigation';
import { useSmoothScroll } from '@/src/hooks/useSmoothScroll.jsx';
import { useSmoothScrollOnRouteChange } from '@/src/hooks/smoothScroll.js';
import { ShowSettingProvider } from '@/src/components/context/ShowSettingProvider.jsx';

// Register GSAP plugins once
const registerGsapPlugins = () => {
	if (!gsap?.utils?.checkPrefix) {
		gsap.registerPlugin(
			ScrollTrigger,
			ScrollSmoother,
			ScrollToPlugin,
			SplitText,
			DrawSVGPlugin
		);
	}
};
registerGsapPlugins();

export default function RootLayout({ children }) {
	const pathName = usePathname();
	const dispatch = DispatchCursor();
	const isMobile = useDeviceTypeMobile();

	// Loading states
	const [isLoading, setIsLoading] = useState(true);
	const [dataLoaded, setDataLoaded] = useState(false);

	// Initialize smooth scroll
	const smoothRef = useSmoothScroll(isMobile, isLoading, () => setIsLoading(false));

	// Load images and fonts once after mount
	useEffect(() => {
		let isMounted = true;

		const loadAssets = async () => {
			try {
				const images = Array.from(document.images);

				await Promise.all(
					images.map(
						(img) =>
							img.complete
								? Promise.resolve()
								: new Promise((resolve) => {
									const onLoadOrError = () => {
										img.removeEventListener('load', onLoadOrError);
										img.removeEventListener('error', onLoadOrError);
										resolve();
									};
									img.addEventListener('load', onLoadOrError);
									img.addEventListener('error', onLoadOrError);
								})
					)
				);

				if (document.fonts?.ready) await document.fonts.ready;

				if (isMounted) setDataLoaded(true);
			} catch (err) {
				console.error('Error loading assets:', err);
				if (isMounted) setDataLoaded(true);
			}
		};

		loadAssets();

		return () => {
			isMounted = false;
		};
	}, []);

	// Custom hooks
	useAnimatedTextTitle([pathName]);
	useSmoothScrollOnRouteChange(pathName);
	useAppEffects();

	return (
		<ShowSettingProvider getMenu={getMenuData} getSettings={getSettingsData}>
			<html lang="en" key={pathName}>
			<SEO
				title="ECO Sourcing Limited"
				description="Eco Sourcing Limited believes that good quality has no boundary to go the extra mile to excel and stand out from the competitors."
				keywords=""
				author="Dcastalia LTD"
				url={`https://localhost:3000${pathName}`}
				image="/images/static/logo.svg"
				fav="/images/static/fav.svg"
				twitterHandle=""
				facebookVerification=""
				googleVerification=""
				canonical={`https://localhost:3000${pathName}`}
			/>
			<body suppressHydrationWarning>
			<StyledComponentsRegistry>
				<ThemeProvider theme={theme}>
					<ErrorBoundary>
						<HasprCursor>
							<GlobalStyle />
							{isLoading && <Loading />}
							<MenuComponent />
							<Toaster
								duration={5000}
								position="bottom-right"
								className="Toastify__toast-container"
								richColors
							/>
							<div className="App" ref={smoothRef} id="smooth-wrapper">
								<div id="smooth-content">
									{React.Children.map(children, (child) =>
										React.cloneElement(child, { el: smoothRef })
									)}
									<Footer />
								</div>
							</div>
						</HasprCursor>
					</ErrorBoundary>
				</ThemeProvider>
			</StyledComponentsRegistry>
			</body>
			</html>
		</ShowSettingProvider>
	);
}
